import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getActiveMatches, getRawHistoryForStats, getSyncStatus } from '../../services/api';
import MatchRow from './MatchRow';
import BettingAdminPanel from './BettingAdminPanel';
import StatsOverlay from '../StatsOverlay';
import Modal from '../Modal';
import PickemModal from './PickemModal';
import { calculateUserStats } from '../../utils/statsCalculator';

export default function BettingModal({ profile, onBet }) {
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdminView, setIsAdminView] = useState(false);

    const [selectedTourneyId, setSelectedTourneyId] = useState(null);
    const [expandedTourneyIds, setExpandedTourneyIds] = useState(new Set());

    const [showStats, setShowStats] = useState(false);
    const [rawHistory, setRawHistory] = useState([]);
    const [timeframe, setTimeframe] = useState('alltime');
    const [pickemTourneyId, setPickemTourneyId] = useState(null);

    const [syncStatus, setSyncStatus] = useState(null);

    const openStats = async () => {
        try {
            const res = await getRawHistoryForStats();
            setRawHistory(res.data);
            setShowStats(true);
        } catch (e) {
            console.error('Ошибка загрузки статистики', e);
        }
    };

    const stats = useMemo(() => calculateUserStats(rawHistory, null, timeframe), [rawHistory, timeframe]);

    const fetchSyncStatus = useCallback(() => {
        getSyncStatus()
            .then(res => {
                setSyncStatus(res.data);
            })
            .catch(err => console.error('Ошибка получения статуса синхронизации:', err));
    }, []);

    const fetchData = useCallback(() => {
        setLoading(true);
        getActiveMatches()
            .then(res => {
                const list = res.data || [];
                setTournaments(list);

                const now = new Date();
                const yesterday = new Date(now);
                yesterday.setDate(now.getDate() - 1);

                const activeIds = list.filter(tourney => {
                    const activeMatches = (tourney.matches || []).filter(m => !m.winnerId);
                    const finishedMatches = (tourney.matches || []).filter(m => m.winnerId);
                    const end = new Date(tourney.endDate);

                    if (end < yesterday) return false;
                    if (activeMatches.length === 0 && finishedMatches.length === 0) return false;
                    return true;
                }).map(t => t.id);

                setExpandedTourneyIds(new Set(activeIds));
            })
            .catch(console.error)
            .finally(() => setLoading(false));

        // Вызываем статус сразу при первой загрузке матчей
        fetchSyncStatus();

        if (onBet) onBet();
    }, [onBet, fetchSyncStatus]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            fetchSyncStatus();
        }, 15000);

        // Очищаем таймер при размонтировании модалки
        return () => clearInterval(intervalId);
    }, [fetchSyncStatus]);

    useEffect(() => {
        if (selectedTourneyId) {
            const modalContainer = document.querySelector('.modal-content');
            if (modalContainer) {
                modalContainer.scrollTo({ top: 0, behavior: 'instant' });
            }
        }
    }, [selectedTourneyId]);

    const toggleAccordion = (id, e) => {
        e.stopPropagation();
        setExpandedTourneyIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const formatDateRange = (start, end) => {
        if (!start || !end) return '';
        const startDate = new Date(start);
        const endDate = new Date(end);
        const options = { day: 'numeric', month: 'long' };
        if (start === end) return `${startDate.toLocaleDateString('ru-RU', options)}`;
        return `${startDate.toLocaleDateString('ru-RU', options)} - ${endDate.toLocaleDateString('ru-RU', options)}`;
    };

    // Функция для форматирования времени "через X минут/миллисекунд"
    const formatRelativeTime = (targetTimeISO) => {
        if (!targetTimeISO) return 'не запланировано';

        const diffMs = new Date(targetTimeISO) - new Date();
        const diffMinutes = Math.round(diffMs / 60000);

        if (Math.abs(diffMinutes) < 1) {
            return 'меньше минуты';
        }

        const rtf = new Intl.RelativeTimeFormat('ru', { numeric: 'always' });
        return rtf.format(diffMinutes, 'minute');
    };

    // Функция для красивого вывода длительности в сек/мс
    const formatDuration = (ms) => {
        if (!ms) return '0 мс';
        if (ms < 1000) return `${ms} мс`;
        return `${(ms / 1000).toFixed(2)} сек`;
    };

    const SyncIndicator = ({ data }) => {
        if (!data) return null;

        const {
            status,
            trackedTournamentsCount,
            lastSyncTime,
            lastDurationMs,
            nextSyncTime
        } = data;

        const isRunning = status === 'running';

        const formattedLastTime = lastSyncTime
            ? new Date(lastSyncTime).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
            : 'нет данных';

        return (
            <div className="sync-container">
                <div className={`sync-dot ${isRunning ? 'is-running' : 'is-idle'}`}></div>

                <div className="sync-tooltip">
                    <div className="tooltip-item">
                        <strong>Статус:</strong> {isRunning ? 'Выполняется' : 'Ожидание'}
                    </div>
                    <div className="tooltip-item">
                        <strong>Отслеживаемых турниров:</strong> {trackedTournamentsCount ?? 0}
                    </div>
                    <div className="tooltip-item">
                        <strong>Последнее обновление:</strong> {formattedLastTime}
                    </div>
                    <div className="tooltip-item">
                        <strong>Длительность:</strong> {formatDuration(lastDurationMs)}
                    </div>
                    <div className="tooltip-item">
                        <strong>Следующее обновление:</strong> {formatRelativeTime(nextSyncTime)}
                    </div>
                </div>
            </div>
        );
    };

    // Краткий и понятный счетчик
    const getMatchCountsLabel = (activeCount, finishedCount) => {
        if (activeCount > 0 && finishedCount > 0) {
            return `${activeCount} акт. / ${finishedCount} зав.`;
        }
        if (activeCount > 0) {
            return `${activeCount} акт.`;
        }
        if (finishedCount > 0) {
            return `${finishedCount} зав.`;
        }
        return '0 матчей';
    };

    const { activeTournaments, plannedTournaments, finishedTournaments } = useMemo(() => {
        const active = [];
        const planned = [];
        const finished = [];

        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);

        tournaments.forEach(tourney => {
            const matches = tourney.matches || [];
            const activeMatches = matches.filter(m => !m.winnerId);
            const finishedMatches = matches.filter(m => m.winnerId);
            const end = new Date(tourney.endDate);

            if (end < yesterday) {
                finished.push(tourney);
            } else if (activeMatches.length === 0 && finishedMatches.length === 0) {
                planned.push(tourney);
            } else {
                active.push(tourney);
            }
        });

        return {
            activeTournaments: active,
            plannedTournaments: planned,
            finishedTournaments: finished.reverse()
        };
    }, [tournaments]);

    const selectedTourney = useMemo(() => {
        return tournaments.find(t => t.id === selectedTourneyId);
    }, [tournaments, selectedTourneyId]);

    if (loading) return <div className="loading-screen">Загрузка матчей...</div>;

    if (isAdminView) {
        return (
            <div className="betting-modal">
                <div className="betting-header">
                    <h2>Панель администратора</h2>
                    <button className="admin-toggle-btn" onClick={() => setIsAdminView(false)}>
                        ← К матчам
                    </button>
                </div>
                <BettingAdminPanel />
            </div>
        );
    }

    const renderTournamentAccordion = (tourney) => {
        const isExpanded = expandedTourneyIds.has(tourney.id);
        const matches = tourney.matches || [];
        const activeMatches = matches.filter(m => !m.winnerId);
        const finishedMatches = matches.filter(m => m.winnerId);

        return (
            <div key={tourney.id} className="tourney-accordion-item">
                <div className="tourney-accordion-bar">
                    <div className="tourney-info-click" onClick={() => setSelectedTourneyId(tourney.id)}>
                        <span className="tourney-name">{tourney.name}</span>
                        <span className="tourney-chevron">›</span>
                    </div>

                    <div className="tourney-bar-actions">
                        {tourney.hasPickem && (
                            <button
                                className="tourney-pickem-badge-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPickemTourneyId(tourney.id);
                                }}
                            >
                                Pick'EM
                            </button>
                        )}

                        <button
                            className={`tourney-toggle-btn ${isExpanded ? 'open' : ''}`}
                            onClick={(e) => toggleAccordion(tourney.id, e)}
                        >
                            <span className="count-label">
                                {getMatchCountsLabel(activeMatches.length, finishedMatches.length)}
                            </span>
                            <span className="arrow">{isExpanded ? '⌃' : '⌄'}</span>
                        </button>
                    </div>
                </div>

                {isExpanded && (
                    <div className="tourney-accordion-body">
                        {matches.length === 0 ? (
                            <div className="no-matches-text">Матчи еще не добавлены</div>
                        ) : (
                            <>
                                {activeMatches.map(match => (
                                    <MatchRow key={match.id} match={match} onBetSuccess={fetchData} />
                                ))}

                                {finishedMatches.length > 0 && (
                                    <div className="finished-matches-section">
                                        <h4>Завершенные матчи</h4>
                                        {[...finishedMatches].reverse().map(match => (
                                            <MatchRow key={match.id} match={match} onBetSuccess={fetchData} />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // --- ОТДЕЛЬНЫЙ ЭКРАН ТУРНИРА (Скриншот 2) ---
    if (selectedTourney) {
        const matches = selectedTourney.matches || [];
        const activeMatches = matches.filter(m => !m.winnerId);
        const finishedMatches = matches.filter(m => m.winnerId);

        return (
            <div className="betting-modal tournament-single-view">
                <div className="single-tourney-nav">
                    <button className="nav-back-link" onClick={() => setSelectedTourneyId(null)}>
                        ← Назад к турнирам
                    </button>
                </div>

                {/* Баннер турнира: только картинка на фоне, без градиента/блюра, текст снизу */}
                <div
                    className="tournament-hero-banner"
                    style={{ backgroundImage: selectedTourney.logoUrl ? `url(${selectedTourney.logoUrl})` : 'none' }}
                >
                    {/* Кнопка в правом верхнем углу баннера */}
                    {selectedTourney.hasPickem && (
                        <button
                            className="single-banner-pickem-btn"
                            onClick={() => setPickemTourneyId(selectedTourney.id)}
                        >
                            🎯 Pick'EM
                        </button>
                    )}

                    {/* Название и даты по центру внизу */}
                    <div className="hero-bottom-content">
                        <h2 className="hero-tourney-title">
                            {selectedTourney.pageUrl ? (
                                <a href={selectedTourney.pageUrl} target="_blank" rel="noopener noreferrer">
                                    {selectedTourney.name}
                                </a>
                            ) : selectedTourney.name}
                        </h2>
                        <p className="hero-tourney-dates">
                            {formatDateRange(selectedTourney.startDate, selectedTourney.endDate)}
                        </p>
                    </div>
                </div>

                <div className="matches-list">
                    {matches.length === 0 ? (
                        <p className="no-matches-text">В данном турнире нет матчей.</p>
                    ) : (
                        <>
                            {activeMatches.map(match => (
                                <MatchRow key={match.id} match={match} onBetSuccess={fetchData} />
                            ))}

                            {finishedMatches.length > 0 && (
                                <div className="finished-matches-section">
                                    <h4>Завершенные матчи</h4>
                                    {[...finishedMatches].reverse().map(match => (
                                        <MatchRow key={match.id} match={match} onBetSuccess={fetchData} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <Modal isOpen={!!pickemTourneyId} onClose={() => setPickemTourneyId(null)} size="wide">
                    <PickemModal tournamentId={pickemTourneyId} />
                </Modal>
            </div>
        );
    }

    // --- ГЛАВНЫЙ ЭКРАН ТУРНИРОВ ---
    return (
        <div className="betting-modal">
            <div className="betting-header">
                <div class="betting-header-area">
                    <h2 onClick={openStats} style={{ cursor: 'pointer' }}>𝙝𝙩𝙘𝙥𝙗𝙚𝙩</h2>
                    <SyncIndicator data={syncStatus}/>
                </div>
                {profile?.isAdmin && (
                    <button className="admin-toggle-btn" onClick={() => setIsAdminView(true)}>
                        Панель администратора
                    </button>
                )}
            </div>

            <div className="tournaments-list">
                {tournaments.length === 0 && <p className="no-matches-text">Сейчас нет доступных турниров.</p>}

                {activeTournaments.length > 0 && (
                    <div className="tournaments-group">
                        <h2>Активные турниры</h2>
                        <div className="accordions-container">
                            {activeTournaments.map(renderTournamentAccordion)}
                        </div>
                    </div>
                )}

                {plannedTournaments.length > 0 && (
                    <div className="tournaments-group">
                        <h2>Запланированные турниры</h2>
                        <div className="accordions-container">
                            {plannedTournaments.map(renderTournamentAccordion)}
                        </div>
                    </div>
                )}

                {finishedTournaments.length > 0 && (
                    <div className="tournaments-group">
                        <h2>Завершенные турниры</h2>
                        <div className="accordions-container">
                            {finishedTournaments.map(renderTournamentAccordion)}
                        </div>
                    </div>
                )}
            </div>

            {showStats && (
                <Modal isOpen={showStats} onClose={() => setShowStats(false)} size="narrow">
                    <StatsOverlay
                        title="Статистика беттинга"
                        stats={stats}
                        type="betting"
                        timeframe={timeframe}
                        onTimeframeChange={setTimeframe}
                    />
                </Modal>
            )}
            <Modal isOpen={!!pickemTourneyId} onClose={() => setPickemTourneyId(null)} size="wide">
                <PickemModal tournamentId={pickemTourneyId} />
            </Modal>
        </div>
    );
}